import React, { useState, useMemo } from 'react';
import {
  CaretRight,
  CaretDown,
  Plus,
  PencilSimple,
  Trash,
  Package,
  Lightning,
  Drop,
  PaintBrush,
  Wall,
  Armchair,
  Thermometer,
  Lock,
  SpeakerHigh,
  Sun,
  Wrench,
  Tree,
  Stairs,
  HardHat,
  Cube,
  Shovel,
  ShieldCheck,
  Broadcast,
  Elevator,
  FireExtinguisher,
  WifiHigh,
  GasPump,
  Mountains,
  Fan,
  Blueprint,
} from '@phosphor-icons/react';
import MiniItemPicker from './MiniItemPicker';
import { formatCurrencyShort } from '../../utils/formatters';
import { formatSelectedOption } from '../../utils/boqHelpers';
import styles from './TradeCard.module.css';

// Map trade/category names (Hebrew) to Phosphor icons
// Covers depth-0 BB chapters + common שונות children
const TRADE_ICONS = {
  // ─── Depth-0 chapters ───
  'עבודות מוקדמות': Shovel,
  'עבודות עפר': Mountains,
  'בטון יצוק באתר': Cube,
  'בטון טרום': Cube,
  'עבודות בנייה': Wall,
  איטום: ShieldCheck,
  'נגרות ומסגרות פלדה': Wrench,
  'תברואה ואינסטלציה': Drop,
  חשמל: Lightning,
  טיח: PaintBrush,
  'ריצוף וחיפוי': Wall,
  צבע: PaintBrush,
  אלומיניום: Wall,
  'בטון דרוך': Cube,
  אבן: Cube,
  'מיזוג אוויר': Fan,
  'מערכות חימום': Thermometer,
  מעליות: Elevator,
  תקשורת: WifiHigh,
  'קונסטרוקציות פלדה': HardHat,
  נגרות: Wrench,
  'בטון טרום - מבנים': Cube,
  'רכיבים מתועשים': Cube,
  'כלונסאות וביסוס': Shovel,
  'עוגני קרקע': Shovel,
  'כיבוי אש ובטיחות': FireExtinguisher,
  'בקרת מערכות': Broadcast,
  'אוויר דחוס': Fan,
  'גזים ונוזלים בלחץ': GasPump,
  גנרטור: Lightning,
  'פיתוח נופי': Tree,
  'גינון והשקיה': Tree,
  'קירות תמך': Wall,
  'משטחי בטון': Cube,
  'מים, ביוב ותיעול': Drop,
  מקלטים: ShieldCheck,
  'בטיחות באתר': HardHat,
  // ─── שונות children (own cards) ───
  'ריהוט וציוד': Armchair,
  ריהוט: Armchair,
  'מכשירים ביתיים': Cube,
  תאורה: Sun,
  'גופי תאורה': Sun,
  'נקודת מים': Drop,
  'מתח נמוך': Lightning,
  'חשמל חכם': Lightning,
  נקודות: Lightning,
  'הסקה דירתית': Thermometer,
  'מנעולן ופרזול': Lock,
  'מולטימדיה ותקשורת': SpeakerHigh,
  'מדרגות ואדנים': Stairs,
  חלונות: Wall,
  'כלל הבית': Blueprint,
  'אחרי סיום הבנייה': ShieldCheck,
  'פריטים מותאמים אישית': Wrench,
  אחר: Cube,
};

function getTradeIcon(name) {
  if (!name) return null;
  // Exact match first, then partial
  if (TRADE_ICONS[name]) return TRADE_ICONS[name];
  const key = Object.keys(TRADE_ICONS).find(k => name.includes(k));
  return key ? TRADE_ICONS[key] : null;
}

// Single-line dense item row: name + options | qty × cost | hover actions
function CompactItemRow({ item, masterItems, onEdit, onDelete }) {
  const name =
    item.master_items?.item_name ||
    masterItems?.find(mi => mi.id === item.master_item_id)?.item_name ||
    'פריט';
  const qty = item.quantity || 1;
  const isEstimate = !item.cost_override;
  const options = item.selected_option ? formatSelectedOption(item.selected_option) || null : null;
  const totalCost = parseFloat(item.total_cost) || 0;

  return (
    <div className={styles.itemRow}>
      <div className={styles.itemLeft}>
        <span className={styles.itemName}>{name}</span>
        {options && <span className={styles.itemOptions}>{options}</span>}
      </div>
      <div className={styles.itemRight}>
        {qty > 1 && <span className={styles.itemQty}>{qty}×</span>}
        {totalCost > 0 && (
          <span className={`${styles.itemCost} ${isEstimate ? styles.estimate : ''}`}>
            {formatCurrencyShort(totalCost)}
          </span>
        )}
        <div className={styles.itemActions}>
          <button
            className={styles.itemActionBtn}
            onClick={e => {
              e.stopPropagation();
              onEdit(item.id);
            }}
            title="ערוך"
          >
            <PencilSimple size={14} />
          </button>
          <button
            className={`${styles.itemActionBtn} ${styles.danger}`}
            onClick={e => {
              e.stopPropagation();
              onDelete(item.id);
            }}
            title="מחק"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Group items with the same master_item_id: collapsible header if >1 instance
function SameItemGroup({ masterItemId, items, masterItems, onEdit, onDelete }) {
  const [isOpen, setIsOpen] = useState(false);
  const name =
    items[0]?.master_items?.item_name ||
    masterItems?.find(mi => mi.id === masterItemId)?.item_name ||
    'פריט';
  const groupTotal = useMemo(
    () => items.reduce((sum, i) => sum + (parseFloat(i.total_cost) || 0), 0),
    [items],
  );

  // Single instance — render flat, no sub-group
  if (items.length === 1) {
    return (
      <CompactItemRow
        item={items[0]}
        masterItems={masterItems}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    );
  }

  // Multiple instances — collapsible group
  return (
    <div className={styles.sameItemGroup}>
      <div className={styles.sameItemHeader} onClick={() => setIsOpen(!isOpen)}>
        <div className={styles.sameItemLeft}>
          {isOpen ? <CaretDown size={11} /> : <CaretRight size={11} />}
          <span className={styles.sameItemName}>{name}</span>
          <span className={styles.sameItemBadge}>{items.length}</span>
        </div>
        {groupTotal > 0 && (
          <span className={styles.sameItemCost}>{formatCurrencyShort(groupTotal)}</span>
        )}
      </div>
      {isOpen && (
        <div className={styles.sameItemChildren}>
          {items.map(item => (
            <CompactItemRow
              key={item.id}
              item={item}
              masterItems={masterItems}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Bundle group (simple bundles): collapsible with package icon
function BundleGroupRow({ bundleItems, masterItems, onEdit, onDelete }) {
  const [isOpen, setIsOpen] = useState(false);

  const bundleMasterItemId = bundleItems[0]?.relationships?.bundle?.master_item_id;
  const bundleDisplayName =
    masterItems?.find(mi => mi.id === bundleMasterItemId)?.item_name || 'חבילה';

  const bundleTotalCost = useMemo(
    () => bundleItems.reduce((sum, i) => sum + (parseFloat(i.total_cost) || 0), 0),
    [bundleItems],
  );

  return (
    <div className={styles.bundleGroup}>
      <div className={styles.bundleHeader} onClick={() => setIsOpen(!isOpen)}>
        <div className={styles.bundleHeaderLeft}>
          {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
          <Package size={14} />
          <span>{bundleDisplayName}</span>
          <span className={styles.bundleBadge}>{bundleItems.length}</span>
        </div>
        {bundleTotalCost > 0 && (
          <span className={styles.bundleCost}>{formatCurrencyShort(bundleTotalCost)}</span>
        )}
      </div>
      {isOpen && (
        <div className={styles.bundleChildren}>
          {bundleItems.map(item => (
            <CompactItemRow
              key={item.id}
              item={item}
              masterItems={masterItems}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TradeCard({
  vendorType,
  items,
  masterItems,
  suggestedItems,
  projectItems,
  onDelete,
  onEdit,
  onAddItems,
  isLoading,
  windowsContent,
  windowCount,
  onAddWindow,
  bbGroupMap,
  missingSuggestedCount = 0,
  currentCount = 0,
  variant = 'default', // 'default' or 'catalog'
  expandedKey = null, // Controlled accordion: which card is open
  onToggleExpand = null, // Controlled accordion: callback to toggle
}) {
  const isCatalog = variant === 'catalog';
  const hasItems = items && items.length > 0;
  const cardKey = vendorType?.id || '__unknown__';

  // Controlled accordion mode if parent provides onToggleExpand
  const isControlled = onToggleExpand !== null;
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = isControlled ? expandedKey === cardKey : localExpanded;
  const setIsExpanded = isControlled
    ? open => onToggleExpand(open ? cardKey : null)
    : setLocalExpanded;

  const visibleItems = useMemo(() => {
    if (!items) return [];
    return items.filter(i => i.relationships?.bundle?.role !== 'parent');
  }, [items]);

  const itemCount = visibleItems.length;

  const totalCost = useMemo(() => {
    return visibleItems.reduce((sum, i) => sum + (parseFloat(i.total_cost) || 0), 0);
  }, [visibleItems]);

  // Separate items into bundles vs standalone, then group standalone by master_item_id
  const { bundleGroups, sameItemGroups } = useMemo(() => {
    const bundleMap = {};
    const standalone = [];

    visibleItems.forEach(item => {
      const bundleName = item.relationships?.bundle?.name;
      const bundleMasterItemId = item.relationships?.bundle?.master_item_id;
      if (bundleName && bundleMasterItemId) {
        // Choice bundle children show as standalone
        const parentBundle = masterItems?.find(mi => mi.id === bundleMasterItemId);
        const isChoiceBundle = parentBundle?.bundle_type === 'choice';
        if (isChoiceBundle) {
          standalone.push(item);
        } else {
          if (!bundleMap[bundleName]) bundleMap[bundleName] = [];
          bundleMap[bundleName].push(item);
        }
      } else {
        standalone.push(item);
      }
    });

    // Group standalone items by master_item_id for same-item stacking
    const itemMap = new Map();
    standalone.forEach(item => {
      const key = item.master_item_id || item.id;
      if (!itemMap.has(key)) itemMap.set(key, []);
      itemMap.get(key).push(item);
    });

    return {
      bundleGroups: Object.entries(bundleMap),
      sameItemGroups: Array.from(itemMap.entries()), // [[masterItemId, items[]]]
    };
  }, [visibleItems, masterItems]);

  // Items available for this card's picker:
  // Filter by BB group key and suggested items for this space
  const { filteredMasterItems, filteredSuggested } = useMemo(() => {
    if (!masterItems || !vendorType) return { filteredMasterItems: [], filteredSuggested: [] };

    const suggestedMasterIds = new Set((suggestedItems || []).map(si => si.master_item_id));
    const cardKey = vendorType.id;

    // Match master items to this card's BB group
    const groupFilter = mi => {
      if (cardKey === '__other__') return !mi.bb_chapter_id;
      if (!bbGroupMap || !mi.bb_chapter_id) return false;
      return bbGroupMap[mi.bb_chapter_id]?.key === cardKey;
    };

    const hasSuggestions = suggestedItems && suggestedItems.length > 0;
    const filtered = masterItems.filter(
      mi => groupFilter(mi) && (!hasSuggestions || suggestedMasterIds.has(mi.id)),
    );

    const suggested = (suggestedItems || []).filter(si => {
      const mi = masterItems.find(m => m.id === si.master_item_id);
      return mi && groupFilter(mi);
    });

    return { filteredMasterItems: filtered, filteredSuggested: suggested };
  }, [masterItems, vendorType, suggestedItems, bbGroupMap]);

  // ─── Catalog variant: compact pill with inline picker ───────────
  if (isCatalog) {
    const togglePicker = () => onToggleExpand(isExpanded ? null : cardKey);
    const pickerOpen = isExpanded;

    const pillClasses = [
      styles.catalogPill,
      currentCount > 0 ? styles.catalogPillActive : '',
      pickerOpen ? styles.catalogPillOpen : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={styles.catalogCard}>
        <button
          className={pillClasses}
          onClick={() => {
            if (windowsContent && onAddWindow) onAddWindow();
            else togglePicker();
          }}
          aria-expanded={pickerOpen}
        >
          {(() => {
            const Icon = getTradeIcon(vendorType?.name);
            return Icon ? (
              <Icon size={16} weight={currentCount > 0 ? 'fill' : 'regular'} />
            ) : (
              <Plus size={16} />
            );
          })()}
          <span className={styles.catalogPillName}>{vendorType?.name || 'אחר'}</span>
          {missingSuggestedCount > 0 && (
            <span className={styles.catalogSuggestedBadge} title="פריטים מומלצים שלא הוספו">
              {missingSuggestedCount === 1
                ? '1 פריט מומלץ'
                : `${missingSuggestedCount} פריטים מומלצים`}
            </span>
          )}
          {!windowsContent && (
            <span className={styles.catalogCaret} aria-hidden="true">
              <CaretDown size={12} />
            </span>
          )}
        </button>
        {pickerOpen && !windowsContent && (
          <div className={styles.catalogPickerWrap}>
            <MiniItemPicker
              masterItems={filteredMasterItems}
              suggestedItems={filteredSuggested}
              projectItems={projectItems}
              onAddItems={onAddItems}
              isLoading={isLoading}
              onClose={() => onToggleExpand(null)}
            />
          </div>
        )}
      </div>
    );
  }

  // ─── Default variant: full card with items ─────────────────────
  return (
    <div className={`${styles.vendorCard} ${isExpanded ? styles.expanded : ''}`}>
      <div
        className={styles.cardHeader}
        onClick={() => (hasItems || windowsContent) && setIsExpanded(!isExpanded)}
        style={{ cursor: hasItems || windowsContent ? 'pointer' : 'default' }}
      >
        <div className={styles.headerLeft}>
          {(hasItems || windowsContent) && (
            <span className={styles.expandIcon}>
              <CaretRight size={14} />
            </span>
          )}
          {(() => {
            const Icon = getTradeIcon(vendorType?.name);
            return Icon ? <Icon size={15} className={styles.tradeIcon} /> : null;
          })()}
          <span className={styles.vendorName}>{vendorType?.name || 'אחר'}</span>
          {(windowCount !== undefined ? windowCount : itemCount) > 0 && (
            <span className={styles.badge}>
              {windowCount !== undefined ? windowCount : itemCount}
            </span>
          )}
        </div>
        <div className={styles.headerRight}>
          {totalCost > 0 && (
            <span className={styles.metaValue}>{formatCurrencyShort(totalCost)}</span>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className={styles.cardBody}>
          {windowsContent ? (
            windowsContent
          ) : (
            <>
              {visibleItems.length > 0 && (
                <div className={styles.itemList}>
                  {sameItemGroups.map(([masterItemId, groupItems]) => (
                    <SameItemGroup
                      key={masterItemId}
                      masterItemId={masterItemId}
                      items={groupItems}
                      masterItems={masterItems}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                  ))}

                  {bundleGroups.map(([bName, bItems]) => (
                    <BundleGroupRow
                      key={bName}
                      bundleItems={bItems}
                      masterItems={masterItems}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(TradeCard);
